/**
 * Knowledge Module — Hỏi đáp tài liệu (Phase D 2026-05-18)
 *
 * Stack: Markitdown + bge-m3 + Qdrant + Claude Max OAuth.
 * Backend: /api/v1/knowledge/*
 *
 * Layout (Claude.ai-style):
 *   ┌──────────┬──────────────────────────────────┐
 *   │ Sidebar  │  Header (collection name + actions) │
 *   │ Collections│ ────────────────────────────────│
 *   │          │  Tabs: [Documents] [Chat Q&A]    │
 *   │ + New    │  ────────────────────────────────│
 *   │          │  Main content                    │
 *   │  - col1  │                                  │
 *   │  - col2  │                                  │
 *   └──────────┴──────────────────────────────────┘
 */
import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import {
    Box, Typography, TextField, IconButton, List, ListItemButton, ListItemText,
    Drawer, Chip, CircularProgress, Alert, Button, Divider, Tabs, Tab,
    Dialog, DialogTitle, DialogContent, DialogActions, LinearProgress, Tooltip,
    Paper, Accordion, AccordionSummary, AccordionDetails,
    Select, MenuItem, FormControl, InputLabel, FormHelperText
} from '@mui/material';
import {
    IconBook2, IconUpload, IconMessageCircle, IconFile, IconCircleCheck,
    IconAlertCircle, IconLoader2, IconRefresh, IconSend, IconPlus, IconTrash,
    IconFileTypePdf, IconFileTypeDocx, IconFileTypeXls, IconPhoto, IconChevronDown,
    IconLayoutSidebar, IconQuote
} from '@tabler/icons-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import axios from 'axios';

const api = axios.create({ baseURL: process.env.REACT_APP_API_URL || '/api/v1' });
api.interceptors.request.use((cfg) => {
    const token = localStorage.getItem('serviceToken') || localStorage.getItem('auth_token');
    if (token) cfg.headers.Authorization = `Bearer ${token}`;
    return cfg;
});

const getAuthHeader = () => {
    const t = localStorage.getItem('serviceToken') || localStorage.getItem('auth_token');
    return t ? `Bearer ${t}` : '';
};

const MAX_WIDTH = 820;

const STATUS_COLOR = {
    pending: 'default',
    converting: 'info',
    chunking: 'info',
    embedding: 'info',
    ready: 'success',
    failed: 'error'
};

const STATUS_LABEL = {
    pending: 'Chờ',
    converting: 'Convert',
    chunking: 'Chunk',
    embedding: 'Embed',
    ready: 'Sẵn sàng',
    failed: 'Lỗi'
};

const fileIcon = (filename) => {
    const ext = (filename || '').split('.').pop().toLowerCase();
    if (ext === 'pdf') return <IconFileTypePdf size={18} color="#d32f2f" />;
    if (['docx', 'doc'].includes(ext)) return <IconFileTypeDocx size={18} color="#1976d2" />;
    if (['xlsx', 'xls', 'csv'].includes(ext)) return <IconFileTypeXls size={18} color="#388e3c" />;
    if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) return <IconPhoto size={18} color="#7b1fa2" />;
    return <IconFile size={18} color="#616161" />;
};

const fmtSize = (n) => {
    if (!n) return '0';
    if (n < 1024) return `${n}B`;
    if (n < 1024 ** 2) return `${(n / 1024).toFixed(1)}K`;
    if (n < 1024 ** 3) return `${(n / 1024 ** 2).toFixed(1)}M`;
    return `${(n / 1024 ** 3).toFixed(2)}G`;
};

// ──────────────────────────────────────────────────────
// Markdown renderer (giống Agent Hub)
// ──────────────────────────────────────────────────────
const Md = ({ children }) => (
    <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
            code({ inline, className, children, ...props }) {
                const m = /language-(\w+)/.exec(className || '');
                return !inline && m ? (
                    <SyntaxHighlighter style={vscDarkPlus} language={m[1]} PreTag="div"
                        customStyle={{ borderRadius: 8, fontSize: 13, margin: '12px 0' }} {...props}>
                        {String(children).replace(/\n$/, '')}
                    </SyntaxHighlighter>
                ) : (
                    <code style={{ background: '#f1f3f5', padding: '2px 6px', borderRadius: 4, fontSize: '0.92em', fontFamily: 'ui-monospace,SFMono-Regular,Menlo,monospace' }} className={className} {...props}>{children}</code>
                );
            },
            p: ({ children }) => <Typography component="p" sx={{ my: 1, lineHeight: 1.7, fontSize: 14.5 }}>{children}</Typography>,
            h1: ({ children }) => <Typography variant="h6" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>{children}</Typography>,
            h2: ({ children }) => <Typography variant="subtitle1" sx={{ mt: 1.5, mb: 0.5, fontWeight: 600 }}>{children}</Typography>,
            h3: ({ children }) => <Typography variant="subtitle2" sx={{ mt: 1, mb: 0.5, fontWeight: 600 }}>{children}</Typography>,
            ul: ({ children }) => <Box component="ul" sx={{ pl: 3, my: 1 }}>{children}</Box>,
            ol: ({ children }) => <Box component="ol" sx={{ pl: 3, my: 1 }}>{children}</Box>,
            li: ({ children }) => <Box component="li" sx={{ my: 0.3, lineHeight: 1.7 }}>{children}</Box>
        }}
    >{children || ''}</ReactMarkdown>
);

// ──────────────────────────────────────────────────────
// SSE stream chat
// ──────────────────────────────────────────────────────
async function streamKnowledgeChat({ collectionId, question, onRetrieval, onText, onDone, onError, onCitations }) {
    const baseUrl = (process.env.REACT_APP_API_URL || '/api/v1');
    const res = await fetch(`${baseUrl}/knowledge/collections/${collectionId}/chat/stream`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: getAuthHeader() },
        body: JSON.stringify({ question })
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    if (!res.body) throw new Error('No body');

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buf = '';
    while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        while (true) {
            const idx = buf.indexOf('\n\n');
            if (idx < 0) break;
            const block = buf.slice(0, idx);
            buf = buf.slice(idx + 2);
            let event = 'message';
            let data = null;
            for (const line of block.split('\n')) {
                if (line.startsWith('event:')) event = line.slice(6).trim();
                else if (line.startsWith('data:')) {
                    try { data = JSON.parse(line.slice(5).trim()); } catch { data = null; }
                }
            }
            if (data == null) continue;
            if (event === 'retrieval') onRetrieval?.(data);
            else if (event === 'text') onText?.(data.delta || '');
            else if (event === 'done') { onCitations?.(data.citations || []); onDone?.(data); }
            else if (event === 'error') onError?.(data);
        }
    }
}

// ──────────────────────────────────────────────────────
// Components
// ──────────────────────────────────────────────────────
const NewCollectionDialog = ({ open, onClose, onCreated, projectId }) => {
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [err, setErr] = useState(null);
    const [options, setOptions] = useState({ embedding_models: [], llm_models: [] });
    const [embeddingModel, setEmbeddingModel] = useState('BAAI/bge-m3');
    const [llmModel, setLlmModel] = useState('claude-sonnet-4-6');

    // Fetch options khi dialog mở
    useEffect(() => {
        if (!open) return;
        let mounted = true;
        api.get('/knowledge/options')
            .then((res) => {
                if (!mounted) return;
                setOptions(res.data || { embedding_models: [], llm_models: [] });
                const defEmb = res.data?.embedding_models?.find((m) => m.default);
                const defLlm = res.data?.llm_models?.find((m) => m.default);
                if (defEmb) setEmbeddingModel(defEmb.id);
                if (defLlm) setLlmModel(defLlm.id);
            })
            .catch((e) => console.warn('Cannot load knowledge options', e));
        return () => { mounted = false; };
    }, [open]);

    const handleSubmit = async () => {
        if (!name.trim()) return;
        setSubmitting(true);
        try {
            const res = await api.post('/knowledge/collections', {
                name: name.trim(),
                description: description.trim() || null,
                project_id: projectId || null,
                embedding_model: embeddingModel,
                llm_model: llmModel
            });
            onCreated(res.data);
            setName('');
            setDescription('');
            onClose();
        } catch (e) {
            setErr(e?.response?.data?.detail || e?.message);
        } finally {
            setSubmitting(false);
        }
    };

    const embeddingMeta = options.embedding_models.find((m) => m.id === embeddingModel);
    const llmMeta = options.llm_models.find((m) => m.id === llmModel);

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Tạo Knowledge Collection mới</DialogTitle>
            <DialogContent>
                <TextField autoFocus fullWidth label="Tên collection" placeholder="VD: Báo chí & sự tự tin lãnh đạo VN30"
                    value={name} onChange={(e) => setName(e.target.value)} sx={{ mt: 1 }} />
                <TextField fullWidth multiline rows={3} label="Mô tả (tuỳ chọn)"
                    value={description} onChange={(e) => setDescription(e.target.value)} sx={{ mt: 2 }} />

                <FormControl fullWidth sx={{ mt: 2 }} size="small">
                    <InputLabel id="lbl-emb">Embedding model</InputLabel>
                    <Select
                        labelId="lbl-emb"
                        value={embeddingModel}
                        label="Embedding model"
                        onChange={(e) => setEmbeddingModel(e.target.value)}
                    >
                        {options.embedding_models.map((m) => (
                            <MenuItem key={m.id} value={m.id}>
                                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{m.label}</Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {m.dim}d · {m.lang} · ~{m.size_gb}GB cache
                                    </Typography>
                                </Box>
                            </MenuItem>
                        ))}
                    </Select>
                    <FormHelperText>
                        {embeddingMeta ? `${embeddingMeta.dim} dimensions · ${embeddingMeta.lang}` : ''}
                        {embeddingMeta?.default && ' · KHÔNG đổi sau khi tạo (gắn với vectors trong Qdrant)'}
                    </FormHelperText>
                </FormControl>

                <FormControl fullWidth sx={{ mt: 2 }} size="small">
                    <InputLabel id="lbl-llm">LLM model (Q&A)</InputLabel>
                    <Select
                        labelId="lbl-llm"
                        value={llmModel}
                        label="LLM model (Q&A)"
                        onChange={(e) => setLlmModel(e.target.value)}
                    >
                        {options.llm_models.map((m) => (
                            <MenuItem key={m.id} value={m.id}>
                                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{m.label}</Typography>
                                    <Typography variant="caption" color="text.secondary">cost: {m.cost}</Typography>
                                </Box>
                            </MenuItem>
                        ))}
                    </Select>
                    <FormHelperText>
                        LLM dùng cho chat Q&A. Có thể đổi sau (chỉ ảnh hưởng chat tới, không re-embed).
                    </FormHelperText>
                </FormControl>

                {err && <Alert severity="error" sx={{ mt: 2 }}>{err}</Alert>}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Huỷ</Button>
                <Button variant="contained" onClick={handleSubmit} disabled={!name.trim() || submitting}>
                    {submitting ? <CircularProgress size={18} /> : 'Tạo'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

const DocumentRow = ({ doc, onDelete }) => {
    const status = (doc.status || '').toLowerCase();
    const isProcessing = ['converting', 'chunking', 'embedding', 'pending'].includes(status);
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.25, borderBottom: '1px solid', borderColor: 'grey.100' }}>
            {fileIcon(doc.filename)}
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {doc.filename}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 0.3 }}>
                    <Chip size="small" label={STATUS_LABEL[status] || status}
                        color={STATUS_COLOR[status] || 'default'} sx={{ height: 18, fontSize: 10 }} />
                    {doc.chunk_count > 0 && <Typography variant="caption" color="text.secondary">{doc.chunk_count} chunks</Typography>}
                    <Typography variant="caption" color="text.secondary">{fmtSize(doc.size_bytes)}</Typography>
                </Box>
                {isProcessing && (
                    <LinearProgress variant="determinate" value={doc.progress || 0} sx={{ mt: 0.5, height: 3, borderRadius: 1 }} />
                )}
                {status === 'failed' && doc.error_message && (
                    <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.3 }}>
                        {doc.error_message.slice(0, 200)}
                    </Typography>
                )}
            </Box>
            <Tooltip title="Xoá document">
                <IconButton size="small" onClick={() => onDelete(doc)} disabled={isProcessing}>
                    <IconTrash size={16} />
                </IconButton>
            </Tooltip>
        </Box>
    );
};

const CitationCard = ({ c, idx }) => (
    <Paper variant="outlined" sx={{ p: 1.25, mb: 1, borderRadius: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Chip size="small" label={`[${idx}]`} sx={{ height: 18, fontSize: 10, fontWeight: 600 }} />
            <IconQuote size={13} />
            <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
                doc {c.doc_id} · chunk {c.chunk_idx} · score {c.score?.toFixed(3)}
            </Typography>
        </Box>
        {c.heading_path && (
            <Typography variant="caption" sx={{ display: 'block', fontStyle: 'italic', color: 'text.secondary' }}>
                {c.heading_path}
            </Typography>
        )}
    </Paper>
);

// ──────────────────────────────────────────────────────
// Main
// ──────────────────────────────────────────────────────
export default function KnowledgePage() {
    const [collections, setCollections] = useState([]);
    const [selected, setSelected] = useState(null);
    const [documents, setDocuments] = useState([]);
    const [tab, setTab] = useState(0);  // 0=documents, 1=chat
    const [sidebarOpen, setSidebarOpen] = useState(true);
    const [newDialog, setNewDialog] = useState(false);
    const [error, setError] = useState(null);
    const [uploading, setUploading] = useState(false);
    const fileInputRef = useRef(null);
    const pollRef = useRef(null);

    // Chat state
    const [messages, setMessages] = useState([]);
    const [chatInput, setChatInput] = useState('');
    const [streaming, setStreaming] = useState(null);
    const [chatLoading, setChatLoading] = useState(false);
    const scrollRef = useRef(null);

    // Load collections
    const loadCollections = useCallback(async () => {
        try {
            const res = await api.get('/knowledge/collections');
            const list = res.data || [];
            setCollections(list);
            if (!selected && list.length > 0) setSelected(list[0]);
        } catch (e) {
            setError(e?.message || 'Cannot load');
        }
    }, [selected]);

    useEffect(() => { loadCollections(); }, []);

    // Load documents for selected collection + poll khi có processing
    const loadDocuments = useCallback(async (id) => {
        try {
            const res = await api.get(`/knowledge/collections/${id}/documents`);
            const list = res.data || [];
            setDocuments(list);
            // Tự poll nếu có document đang process
            const hasProcessing = list.some((d) => ['pending', 'converting', 'chunking', 'embedding'].includes((d.status || '').toLowerCase()));
            if (hasProcessing && !pollRef.current) {
                pollRef.current = setInterval(() => loadDocuments(id), 3000);
            } else if (!hasProcessing && pollRef.current) {
                clearInterval(pollRef.current);
                pollRef.current = null;
            }
        } catch (e) {
            setError(e?.message || 'Cannot load docs');
        }
    }, []);

    useEffect(() => {
        if (pollRef.current) {
            clearInterval(pollRef.current);
            pollRef.current = null;
        }
        if (selected) {
            loadDocuments(selected.id);
            setMessages([]);
            setStreaming(null);
        }
        return () => {
            if (pollRef.current) {
                clearInterval(pollRef.current);
                pollRef.current = null;
            }
        };
    }, [selected?.id, loadDocuments]);

    useEffect(() => {
        if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }, [messages, streaming]);

    // Upload files
    const handleFiles = async (files) => {
        if (!selected || !files?.length) return;
        setUploading(true);
        try {
            for (const file of files) {
                const fd = new FormData();
                fd.append('file', file);
                await fetch(`${process.env.REACT_APP_API_URL || '/api/v1'}/knowledge/collections/${selected.id}/documents`, {
                    method: 'POST', headers: { Authorization: getAuthHeader() }, body: fd
                });
            }
            loadDocuments(selected.id);
        } catch (e) {
            setError(e?.message || 'Upload failed');
        } finally {
            setUploading(false);
        }
    };

    const handleDeleteDoc = async (doc) => {
        if (!window.confirm(`Xoá ${doc.filename}?`)) return;
        try {
            await api.delete(`/knowledge/collections/${selected.id}/documents/${doc.id}`);
            loadDocuments(selected.id);
        } catch (e) {
            setError(e?.message || 'Delete failed');
        }
    };

    const handleDeleteCollection = async () => {
        if (!selected) return;
        if (!window.confirm(`Xoá collection "${selected.name}"? Toàn bộ documents + vectors sẽ mất.`)) return;
        try {
            await api.delete(`/knowledge/collections/${selected.id}`);
            setSelected(null);
            loadCollections();
        } catch (e) {
            setError(e?.message || 'Delete failed');
        }
    };

    const sendChat = async (text) => {
        if (!text.trim() || !selected || chatLoading) return;
        setMessages((m) => [...m, { role: 'user', content: text }]);
        setChatInput('');
        setChatLoading(true);
        setStreaming({ role: 'assistant', content: '', retrieval: null, citations: [] });

        try {
            await streamKnowledgeChat({
                collectionId: selected.id,
                question: text,
                onRetrieval: (d) => setStreaming((cur) => ({ ...cur, retrieval: d })),
                onText: (delta) => setStreaming((cur) => ({ ...cur, content: (cur?.content || '') + delta })),
                onCitations: (cits) => setStreaming((cur) => ({ ...cur, citations: cits })),
                onDone: (d) => {
                    setStreaming((cur) => {
                        const final = { ...cur, usage: d?.usage };
                        setMessages((m) => [...m, final]);
                        return null;
                    });
                },
                onError: (d) => { setError(d?.detail || 'Stream error'); setStreaming(null); }
            });
        } catch (e) {
            setError(e?.message || 'Chat failed');
            setStreaming(null);
        } finally {
            setChatLoading(false);
        }
    };

    const readyDocCount = useMemo(() => documents.filter((d) => d.status === 'ready').length, [documents]);

    return (
        <Box sx={{ display: 'flex', height: 'calc(100vh - 88px)', bgcolor: '#fff' }}>
            {/* Sidebar Collections */}
            {sidebarOpen && (
                <Paper elevation={0} sx={{ width: 300, borderRight: '1px solid', borderColor: 'grey.200', display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'grey.200', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box>
                            <Typography sx={{ fontWeight: 600, fontSize: 16 }}>Knowledge</Typography>
                            <Typography variant="caption" color="text.secondary">{collections.length} collections</Typography>
                        </Box>
                        <Tooltip title="Tạo collection mới">
                            <IconButton size="small" color="primary" onClick={() => setNewDialog(true)}>
                                <IconPlus size={20} />
                            </IconButton>
                        </Tooltip>
                    </Box>
                    <Box sx={{ flex: 1, overflow: 'auto' }}>
                        {collections.length === 0 && (
                            <Box sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>
                                <IconBook2 size={36} style={{ opacity: 0.3 }} />
                                <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>Chưa có collection. Tạo collection đầu tiên.</Typography>
                            </Box>
                        )}
                        <List dense disablePadding>
                            {collections.map((c) => (
                                <ListItemButton key={c.id} selected={selected?.id === c.id} onClick={() => setSelected(c)} sx={{ borderBottom: '1px solid', borderColor: 'grey.100' }}>
                                    <ListItemText
                                        primary={<Typography variant="body2" sx={{ fontSize: 13.5, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.name}</Typography>}
                                        secondary={
                                            <Box sx={{ display: 'flex', gap: 1, mt: 0.3 }}>
                                                <Typography variant="caption" color="text.secondary">{c.doc_count} files</Typography>
                                                <Typography variant="caption" color="text.secondary">·</Typography>
                                                <Typography variant="caption" color="text.secondary">{c.chunk_count} chunks</Typography>
                                            </Box>
                                        }
                                    />
                                </ListItemButton>
                            ))}
                        </List>
                    </Box>
                </Paper>
            )}

            {/* Main */}
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                {/* Header */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5, borderBottom: '1px solid', borderColor: 'grey.200', minHeight: 56 }}>
                    <IconButton size="small" onClick={() => setSidebarOpen((s) => !s)}>
                        <IconLayoutSidebar size={20} />
                    </IconButton>
                    {selected ? (
                        <>
                            <IconBook2 size={20} color="#6366f1" />
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography sx={{ fontWeight: 600, fontSize: 15, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{selected.name}</Typography>
                                <Typography variant="caption" color="text.secondary">{readyDocCount}/{documents.length} sẵn sàng · {selected.chunk_count} chunks · bge-m3</Typography>
                            </Box>
                            <Tooltip title="Refresh">
                                <IconButton size="small" onClick={() => { loadCollections(); loadDocuments(selected.id); }}>
                                    <IconRefresh size={18} />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Xoá collection">
                                <IconButton size="small" color="error" onClick={handleDeleteCollection}>
                                    <IconTrash size={18} />
                                </IconButton>
                            </Tooltip>
                        </>
                    ) : (
                        <Typography color="text.secondary">Chọn collection để bắt đầu</Typography>
                    )}
                </Box>

                {error && <Alert severity="error" onClose={() => setError(null)} sx={{ m: 2 }}>{error}</Alert>}

                {selected && (
                    <>
                        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: '1px solid', borderColor: 'grey.200', px: 2 }}>
                            <Tab icon={<IconFile size={16} />} iconPosition="start" label={`Tài liệu (${documents.length})`} sx={{ minHeight: 42, fontSize: 13 }} />
                            <Tab icon={<IconMessageCircle size={16} />} iconPosition="start" label="Hỏi đáp" sx={{ minHeight: 42, fontSize: 13 }} />
                        </Tabs>

                        {/* Tab Documents */}
                        {tab === 0 && (
                            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                                <Box sx={{ p: 2 }}>
                                    <input ref={fileInputRef} type="file" multiple style={{ display: 'none' }}
                                        accept=".pdf,.docx,.doc,.xlsx,.xls,.csv,.md,.txt,.html,.png,.jpg,.jpeg,.webp"
                                        onChange={(e) => handleFiles(Array.from(e.target.files || []))} />
                                    <Button fullWidth variant="outlined" startIcon={<IconUpload size={18} />}
                                        onClick={() => fileInputRef.current?.click()} disabled={uploading}
                                        sx={{ py: 2, borderStyle: 'dashed', borderWidth: 2 }}>
                                        {uploading ? <><CircularProgress size={16} sx={{ mr: 1 }} />Đang upload...</> : 'Click hoặc kéo thả file (pdf/docx/xlsx/img/...)'}
                                    </Button>
                                </Box>
                                <Box sx={{ flex: 1, overflow: 'auto', borderTop: '1px solid', borderColor: 'grey.100' }}>
                                    {documents.length === 0 && (
                                        <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
                                            <Typography variant="body2">Chưa có tài liệu nào. Upload file để bắt đầu.</Typography>
                                            <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>
                                                Hỗ trợ: PDF · DOCX · XLSX · CSV · Markdown · TXT · Image · HTML
                                            </Typography>
                                        </Box>
                                    )}
                                    {documents.map((d) => <DocumentRow key={d.id} doc={d} onDelete={handleDeleteDoc} />)}
                                </Box>
                            </Box>
                        )}

                        {/* Tab Chat */}
                        {tab === 1 && (
                            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                                <Box ref={scrollRef} sx={{ flex: 1, overflow: 'auto', p: 3, bgcolor: '#fafbfc' }}>
                                    {messages.length === 0 && !streaming && (
                                        <Box sx={{ textAlign: 'center', mt: 6, color: 'text.secondary' }}>
                                            <IconMessageCircle size={40} style={{ opacity: 0.3 }} />
                                            <Typography variant="body2" sx={{ mt: 1 }}>
                                                Hỏi đáp dựa trên {readyDocCount} tài liệu sẵn sàng trong "<strong>{selected.name}</strong>"
                                            </Typography>
                                            {readyDocCount === 0 && (
                                                <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
                                                    ⚠ Chưa có document nào ở trạng thái "Sẵn sàng" — upload + đợi pipeline xử lý xong
                                                </Typography>
                                            )}
                                        </Box>
                                    )}
                                    <Box sx={{ maxWidth: MAX_WIDTH, mx: 'auto' }}>
                                        {messages.map((m, i) => m.role === 'user' ? (
                                            <Box key={i} sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
                                                <Box sx={{ maxWidth: '85%', bgcolor: 'grey.100', borderRadius: 3, px: 2, py: 1.25 }}>
                                                    <Typography sx={{ whiteSpace: 'pre-wrap', fontSize: 14.5 }}>{m.content}</Typography>
                                                </Box>
                                            </Box>
                                        ) : (
                                            <Box key={i} sx={{ mb: 4 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                                                    <Box sx={{ width: 26, height: 26, borderRadius: 1, bgcolor: 'primary.main', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                        <IconBook2 size={16} />
                                                    </Box>
                                                    <Typography sx={{ fontWeight: 600, fontSize: 14 }}>Knowledge Q&A</Typography>
                                                </Box>
                                                <Box sx={{ pl: 4.5 }}>
                                                    <Md>{m.content}</Md>
                                                    {m.citations?.length > 0 && (
                                                        <Accordion disableGutters elevation={0} sx={{ mt: 1, border: '1px solid', borderColor: 'grey.200', '&:before': { display: 'none' } }}>
                                                            <AccordionSummary expandIcon={<IconChevronDown size={16} />} sx={{ minHeight: 36 }}>
                                                                <Typography variant="caption" sx={{ fontWeight: 600 }}>{m.citations.length} citations</Typography>
                                                            </AccordionSummary>
                                                            <AccordionDetails>
                                                                {m.citations.map((c, idx) => <CitationCard key={idx} c={c} idx={idx} />)}
                                                            </AccordionDetails>
                                                        </Accordion>
                                                    )}
                                                    {m.usage && (
                                                        <Box sx={{ mt: 1, display: 'flex', gap: 1.5, color: 'text.secondary', fontSize: 11.5 }}>
                                                            {m.usage.cost_usd > 0 && <span>${m.usage.cost_usd.toFixed(4)}</span>}
                                                            {m.usage.duration_ms > 0 && <span>· {(m.usage.duration_ms / 1000).toFixed(1)}s</span>}
                                                        </Box>
                                                    )}
                                                </Box>
                                            </Box>
                                        ))}
                                        {streaming && (
                                            <Box sx={{ mb: 4 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                                                    <Box sx={{ width: 26, height: 26, borderRadius: 1, bgcolor: 'primary.main', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                        <IconBook2 size={16} />
                                                    </Box>
                                                    <Typography sx={{ fontWeight: 600, fontSize: 14 }}>Knowledge Q&A</Typography>
                                                    {streaming.retrieval && (
                                                        <Chip size="small" label={`${streaming.retrieval.chunk_count} chunks retrieved`} sx={{ height: 18, fontSize: 10 }} />
                                                    )}
                                                </Box>
                                                <Box sx={{ pl: 4.5 }}>
                                                    <Md>{streaming.content || ''}</Md>
                                                    <Box sx={{ display: 'inline-block', width: 8, height: 14, bgcolor: 'primary.main', animation: 'blink 1s infinite' }} />
                                                    <style>{`@keyframes blink { 0%, 50% { opacity: 1; } 51%, 100% { opacity: 0; } }`}</style>
                                                </Box>
                                            </Box>
                                        )}
                                        {chatLoading && !streaming && (
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pl: 4.5, color: 'text.secondary' }}>
                                                <CircularProgress size={14} />
                                                <Typography variant="caption">Đang tìm chunks liên quan...</Typography>
                                            </Box>
                                        )}
                                    </Box>
                                </Box>
                                <Box sx={{ borderTop: '1px solid', borderColor: 'grey.200', p: 2 }}>
                                    <Box sx={{ maxWidth: MAX_WIDTH, mx: 'auto', display: 'flex', alignItems: 'flex-end', gap: 1,
                                              border: '1px solid', borderColor: 'grey.300', borderRadius: 3, px: 1.5, py: 1,
                                              '&:focus-within': { borderColor: 'primary.main' } }}>
                                        <TextField fullWidth multiline maxRows={6}
                                            placeholder={`Hỏi về tài liệu trong "${selected.name}"...`}
                                            value={chatInput} onChange={(e) => setChatInput(e.target.value)}
                                            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendChat(chatInput); } }}
                                            disabled={chatLoading || readyDocCount === 0}
                                            variant="standard"
                                            InputProps={{ disableUnderline: true, sx: { fontSize: 14.5, lineHeight: 1.5 } }} />
                                        <IconButton onClick={() => sendChat(chatInput)} disabled={chatLoading || !chatInput.trim() || readyDocCount === 0}
                                            sx={{ bgcolor: chatInput.trim() ? 'primary.main' : 'grey.300', color: 'white', width: 32, height: 32, '&:hover': { bgcolor: 'primary.dark' }, '&.Mui-disabled': { bgcolor: 'grey.300' } }}>
                                            {chatLoading ? <CircularProgress size={14} color="inherit" /> : <IconSend size={16} />}
                                        </IconButton>
                                    </Box>
                                </Box>
                            </Box>
                        )}
                    </>
                )}
            </Box>

            <NewCollectionDialog open={newDialog} onClose={() => setNewDialog(false)} onCreated={(c) => { setCollections((cs) => [c, ...cs]); setSelected(c); }} />
        </Box>
    );
}
